interface Ticket { url: string; thumbnailUrl?: string | null; expiresIn?: number | null }

/** Renew browser-bound tickets before new range requests need them. */
export function installMediaRenewal(issue: (id: string) => Promise<Ticket>) {
  const busy = new WeakSet<Element>();
  const retryAfter = new WeakMap<Element, number>();
  const nextRenewal = new WeakMap<Element, number>();
  const playIntent = new WeakMap<HTMLMediaElement, boolean>();
  async function renew(el: HTMLMediaElement | HTMLImageElement, force = false) {
    if (busy.has(el) || !el.isConnected || (retryAfter.get(el) || 0) > Date.now()) return;
    let url: URL;
    try { url = new URL(el.src); } catch { return; }
    const match = url.pathname.match(/^\/videos\/[0-9a-f-]{36}\/([0-9a-f-]{36})\/(source|thumbnail)\./i);
    if (!match) return;
    if (el instanceof HTMLImageElement) {
      if (el.complete && el.naturalWidth > 0) return;
      if (url.searchParams.get('v') !== '2' || (!force && Number(url.searchParams.get('exp')) * 1000 > Date.now() + 30_000)) return;
    } else if (!force && (nextRenewal.get(el) || 0) > Date.now()) return;
    busy.add(el);
    const original = el.src;
    try {
      const ticket = await issue(match[1]);
      if (!el.isConnected || el.src !== original) return;
      if (el instanceof HTMLImageElement) {
        if (ticket.thumbnailUrl) el.src = ticket.thumbnailUrl;
      } else if (el.src !== ticket.url || el.error) {
        const time = el.currentTime, playing = playIntent.get(el) ?? (!el.paused && !el.ended), rate = el.playbackRate;
        el.addEventListener('loadedmetadata', () => {
          el.currentTime = time;
          el.playbackRate = rate;
          if (playing) void el.play().catch(() => {});
        }, { once: true });
        el.crossOrigin = 'use-credentials';
        el.src = ticket.url;
        el.load();
      }
      nextRenewal.set(el, ticket.expiresIn === null ? Infinity : Date.now() + 60_000);
      el.removeAttribute('data-media-error');
      el.parentElement?.querySelector('[data-media-renewal-notice]')?.remove();
    } catch (error) {
      if (!el.isConnected || el.src !== original) return;
      retryAfter.set(el, Date.now() + 10_000);
      el.setAttribute('data-media-error', 'Unable to renew playback. Check your connection or membership.');
      // Stop on revoked access; a buffered response cannot itself be recalled.
      if (el instanceof HTMLMediaElement) { playIntent.set(el, false); el.pause(); }
      if (el instanceof HTMLMediaElement && !el.parentElement?.querySelector('[data-media-renewal-notice]')) {
        const notice = document.createElement('p');
        notice.dataset.mediaRenewalNotice = '';
        notice.setAttribute('role', 'alert');
        notice.textContent = 'Playback access could not be renewed. Check your connection and membership, then press play to retry.';
        el.parentElement?.append(notice);
      }
      el.dispatchEvent(new CustomEvent('media-renewal-error', { detail: String(error), bubbles: true }));
    } finally { busy.delete(el); }
  }
  const scan = () => document.querySelectorAll<HTMLMediaElement | HTMLImageElement>('video[src],audio[src],img[src]').forEach(el => void renew(el));
  setInterval(scan, 5000);
  document.addEventListener('visibilitychange', scan);
  document.addEventListener('play', event => {
    if (event.target instanceof HTMLMediaElement) playIntent.set(event.target, true);
    scan();
  }, true);
  document.addEventListener('pause', event => {
    if (event.target instanceof HTMLMediaElement && !event.target.error) playIntent.set(event.target, false);
  }, true);
  document.addEventListener('seeking', scan, true);
  document.addEventListener('error', event => {
    if (event.target instanceof HTMLMediaElement || event.target instanceof HTMLImageElement) void renew(event.target, true);
  }, true);
}
